// The unlock card: what a reached achievement grade looks like in the game
// (achievements.md §4, ui-map-interface.md §8, R936, R962).
//
// THE STAMP (mockup round forty-four, card 10). One DOM card LANDS in the
// top third of the map at `unlock_scale` with a one-shot ring flash, settles to 1×
// over `unlock_land_ms`, holds `unlock_ms`, then TRAVELS over
// `unlock_travel_ms` into the notices column and CONTRACTS into one line
// there through `Overlay.notice`, so the record stays after the card has
// gone. That is the whole shape: the card is the ceremony and the notice
// line is the receipt.
//
// The card therefore has three phases and one life:
// `unlock_land_ms + unlock_ms + unlock_travel_ms`. The queue owns all
// three, and the stylesheet owns what each one looks like.
//
// **The path end is read from the live layout, once.** The notices column
// moves with the form factor, with a notices look (R824) and with the
// dock, so the travel cannot be a rule in a stylesheet. `aim` measures the
// column and the card ONE time, at the start of the travel, and writes the
// delta as two custom properties — never a measurement per frame (§10.1).
//
// Four rules the queue keeps:
//
//  1. **One card at a time**, with `unlock_gap_ms` between two. A fight
//     that reaches three grades must not stack three cards over the map.
//  2. **A burst collapses.** MORE THAN `unlock_collapse_at` unlocks waiting
//     together draw ONE card that counts them, and one notice line each,
//     so a season roll never plays forty cards in a row.
//  3. **Reduced motion keeps the landed card and drops the travel.** The
//     card still lands, still holds and still leaves its line; it fades in
//     place and nothing travels and nothing flashes.
//  4. **Every duration rides a CSS custom property.** The stylesheet names
//     the animations and this writes their lengths, so the hold and the
//     removal cannot drift apart — the rule `ui/notices.ts` already keeps
//     with `--life`.
//
// **The card is text, never markup.** Every word goes in with
// `textContent`: an achievement name is generated from the catalog, and a
// module that writes markup teaches the next one to do the same.
//
// **A tap minimizes the card** (R1442). A tap or a click on a card that
// lands or holds stops the hold and starts the travel at once, so the
// player takes the card off the map and keeps the notice line. The tap
// goes no further than the card. A card that travels takes no pointer,
// because it is on its way out and a second tap belongs to the map.

import { ACHIEVEMENT_GRADES, ACHIEVEMENT_LOOK, type AchievementRow } from '../gen/presentation';
import { achievements as S } from '../gen/strings';
import { wordOf } from '../locale/wordof';
import { formatMetricValue, rowByCode, zoneOf } from './achievements';
import { badgeCanvas } from '../render/badges';
import { reducedMotion } from '../render/motion';
import { isSheet } from './layout';
import { chromeRect } from './uiscale';
import { clamp01 } from '../render/math';

/** One unlock, as the wire states it (`AchievementUnlock`). */
export interface Unlock {
  code: string;
  /** 1 to 6 for a counted achievement; a recognition carries 1. */
  grade: number;
  /** The value that crossed the threshold. */
  value: number;
  /** 0 lifetime, 1 journey, 2 recognition. */
  kind: number;
}

/** The kind field of the wire, named. */
export const KIND_RECOGNITION = 2;

/** The class the notice line and the card both wear, so a theme can find
 *  them and the look blocks of the stylesheet can style one lane. */
export const UNLOCK_CLASS = 'achievement';

/** What the queue needs from the rest of the client. All four are
 *  structural, so a test hands it four functions and no browser. */
export interface UnlockDeps {
  /** Where the card is inserted. The shell (`#ui`) in the game. */
  host: HTMLElement;
  /** The notices column, through `Overlay.notice`. */
  notice(text: string, opts: { cls?: string; key?: string }): void;
  /** The cue, through the audio director. */
  play(id: string): void;
  /** The sheet form factor. `layout.ts` answers in the game. */
  sheet?(): boolean;
  /** Whether the viewer asked for less motion. */
  reduced?(): boolean;
  /** The notices column the card travels into. The game hands over
   *  `#notices`; a test hands over its own element or null. */
  column?(): HTMLElement | null;
}

/** The cue one unlock plays. The hub keeps `sfx-menu-badge` for the dot,
 *  because a dot is furniture and an unlock is news (audio.md §4.4). */
export const UNLOCK_SFX = 'sfx-achievement';

/** The name a player reads for one code. An unknown code reads as its own
 *  code, because a message the server took the trouble to send is never
 *  eaten by a missing row (`events.ts` keeps the same rule). */
export function nameOf(code: string): string {
  return wordOf(S, `${code}_name`, code);
}

/** The description of one code, or empty for a code this build has never
 *  heard of. */
function descOf(code: string): string {
  return wordOf(S, `${code}_desc`, '');
}

/** A recognition, by the wire's kind or by the catalog's: it carries no
 *  grade and no threshold. */
function isRecognition(u: Unlock, row: AchievementRow | null): boolean {
  return u.kind === KIND_RECOGNITION || row?.kind === 'recognition';
}

/** The grade an unlock stands at, 1 or more. */
function gradeOf(u: Unlock): number {
  return Math.max(1, Math.floor(u.grade));
}

/** The name of one category, or the id for a category with no word. */
function categoryName(id: string): string {
  return wordOf(S, `category_${id}`, id);
}

/** The name of one depth zone, 1 to 6. A caller that holds a stored step
 *  turns it into its zone with `zoneOf` first (R1552). */
export function gradeName(zone: number): string {
  const id = ACHIEVEMENT_GRADES[zone - 1];
  if (id === undefined) return S.grade_none;
  return wordOf(S, `grade_${id}`, id.toUpperCase());
}

/**
 * The lines one card carries, as a pure function of the unlock and the
 * catalog. The DOM writer below is the only thing that touches a
 * document, so a test reads the words without one.
 *
 * The card states the description and the figures (R1232): what the
 * player did, the threshold the value crossed, and how far the next grade is.
 */
export interface UnlockLines {
  /** The eyebrow: ACHIEVEMENT and the category, or the count of a
   *  collapsed burst. */
  title: string;
  /** The achievement's own name. */
  name: string;
  /** The description: the code's `_desc` sentence, or empty. */
  desc: string;
  /** The grade and its rank in the ladder, or the recognition label. */
  grade: string;
  /** The value and the threshold it crossed, or empty. */
  figures: string;
  /** The next grade, its threshold and the remainder, or the word for a
   *  ladder that is finished. */
  next: string;
  /** The share of the way from the crossed threshold to the next one,
   *  0 to 1, or null for a card with no ladder. */
  fill: number | null;
  /** The badge to bake: its category and the grade it stands at. */
  category: string;
  badgeGrade: number;
}

/** The one line a card leaves behind in the notices column: the name,
 *  the grade and the value, so the record keeps the figure. */
export function unlockNotice(u: Unlock): string {
  const row = rowByCode(u.code);
  const name = nameOf(u.code);
  if (isRecognition(u, row)) return `${name} · ${S.recognition_label}`;
  const value = formatMetricValue(row?.unit ?? '', u.value);
  return `${name} · ${gradeName(zoneOf(row, gradeOf(u)))} · ${S.row_value({ value })}`;
}

/** The notice key of the handover line, so a second one replaces it. */
const HANDOVER_KEY = 'unlock-handover';

/**
 * The one quiet line of the grades the autopilot reached before the
 * handover (R2127): the names, one each, or '' for no grade.
 */
export function handoverNotice(kept: readonly Unlock[]): string {
  const names = [...new Set(kept.map((u) => nameOf(u.code)))];
  if (names.length === 0) return '';
  return S.unlock_handover({ count: names.length, names: names.join(' · ') });
}

/** What one card says. */
export function linesOf(u: Unlock): UnlockLines {
  const row = rowByCode(u.code);
  const desc = descOf(u.code);
  const name = nameOf(u.code);
  if (isRecognition(u, row)) {
    return {
      title: `${S.unlock_title} · ${categoryName('recognition').toLocaleUpperCase()}`,
      name, desc, grade: S.recognition_label, figures: '', next: '', fill: null,
      category: 'recognition', badgeGrade: 0,
    };
  }
  const grade = gradeOf(u);
  const category = row?.category ?? 'recognition';
  // The figures go through the row's own unit (R949): a `sec` metric
  // counts the player's own seconds and the card reads it back as a span.
  // A code this build has never heard of has no row, so it groups, and it
  // states the value alone, because it knows no threshold.
  const unit = row?.unit ?? '';
  const fmt = (n: number): string => formatMetricValue(unit, n);
  const total = row?.thresholds.length ?? ACHIEVEMENT_GRADES.length;
  const reached = row?.thresholds[grade - 1];
  const next = row?.thresholds[grade];
  // A step reads the name of its zone: the top step of a short lifetime
  // row is Challenger (R1552). `n` and `total` stay the steps.
  const upper = (step: number): string => gradeName(zoneOf(row, step)).toLocaleUpperCase();
  let fill: number | null = null;
  if (row) {
    fill = 1;
    if (next !== undefined) {
      const from = reached ?? 0;
      fill = next > from ? clamp01((u.value - from) / (next - from)) : 0;
    }
  }
  return {
    title: row ? `${S.unlock_title} · ${categoryName(category).toLocaleUpperCase()}` : S.unlock_title,
    name,
    desc,
    grade: row ? S.unlock_grade_of({ grade: upper(grade), n: grade, total }) : upper(grade),
    figures: reached === undefined
      ? S.row_value({ value: fmt(u.value) })
      : S.unlock_reached({ value: fmt(u.value), threshold: fmt(reached) }),
    next: !row
      ? ''
      : next === undefined
        ? S.unlock_complete({ total })
        : S.unlock_next({ grade: upper(grade + 1), value: fmt(next), left: fmt(Math.max(0, next - u.value)) }),
    fill,
    category,
    badgeGrade: zoneOf(row, grade),
  };
}

/** What a collapsed burst says: the count, and no ladder of its own. */
export function collapsedLines(count: number): UnlockLines {
  return {
    title: S.unlock_many({ count }),
    name: '',
    desc: '',
    grade: '',
    figures: '',
    next: '',
    fill: null,
    category: 'recognition',
    badgeGrade: 0,
  };
}

/** The badge on a card, in CSS px. It is smaller than the hub's tile: the
 *  card is chrome over a fight and must not take the map. */
const CARD_BADGE_PX = 48;

/**
 * The queue of unlock cards.
 *
 * A caller pushes an unlock and nothing else. The queue decides when a
 * card stands, when it contracts and when the next one may come.
 */
export class UnlockQueue {
  /** The unlocks waiting for a card. */
  private pending: Unlock[] = [];
  /** The card on screen, or null. */
  private card: HTMLElement | null = null;
  /** The timers this queue owns, so `destroy` can stop every one. */
  private timers = new Set<ReturnType<typeof setTimeout>>();
  /** Whether a drain is already scheduled for this frame. */
  private draining = false;
  /** Whether the `unlock_gap_ms` rest after a card runs now. A push in
   *  the rest waits for its end, which drains the queue (R1342). */
  private resting = false;
  private dead = false;
  /** The grades of the handover while the welcome card stands, or null. */
  private handover: Unlock[] | null = null;
  /** Whether a replay film holds the queue (R2404). */
  private held = false;

  constructor(private readonly deps: UnlockDeps) {}

  /**
   * Files one unlock.
   *
   * Nothing is drawn here. The drain runs on the next macrotask, which is
   * what makes "more than `unlock_collapse_at` in one frame" a question the
   * queue can answer: every unlock of one server frame arrives before it.
   */
  push(u: Unlock): void {
    if (this.dead) return;
    if (this.handover !== null) {
      this.handover.push(u);
      return;
    }
    this.pending.push(u);
    this.schedule();
  }

  /**
   * Starts the handover (R2127, player-reach.md §8.5): while the welcome
   * card stands, a grade the seat reached is the autopilot's work of the
   * season so far, not the player's own act. The queue keeps those grades
   * and draws no card for them. A grade that waits behind a standing card
   * when the hold starts arrived on the same join, so it joins them
   * (R2138); the card that stands plays out.
   */
  holdHandover(): void {
    if (this.handover === null) this.handover = this.pending.splice(0);
  }

  /**
   * Ends the handover: the kept grades leave ONE quiet line in the notices
   * column, with no card and no cue, and the queue draws every later grade
   * as a card again.
   */
  endHandover(): void {
    const kept = this.handover;
    this.handover = null;
    const line = kept ? handoverNotice(kept) : '';
    if (line !== '') this.deps.notice(line, { key: HANDOVER_KEY });
  }

  /**
   * Holds the queue while a replay film runs, or lets it go (R2404). A
   * grade that arrives in the hold waits with no card and no cue, and the
   * queue draws it when the film closes. A card that stands when the hold
   * starts plays out under the film, hidden by the stylesheet, and still
   * leaves its line. The frame loop writes it every frame, so only an edge
   * acts.
   */
  setHeld(on: boolean): void {
    if (this.held === on) return;
    this.held = on;
    if (!on && this.pending.length > 0) this.schedule();
  }

  /** How many unlocks wait for a card. A test reads it; nothing else does. */
  get waiting(): number { return this.pending.length; }

  /** Whether a card stands now. */
  get showing(): boolean { return this.card !== null; }

  /** Takes the card away and forgets every timer. For a teardown. */
  destroy(): void {
    this.dead = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.card?.remove();
    this.card = null;
    this.pending = [];
  }

  private schedule(): void {
    if (this.draining || this.resting || this.card !== null) return;
    this.draining = true;
    this.after(0, () => {
      this.draining = false;
      this.drain();
    });
  }

  /** One timer, kept so a teardown can stop it. */
  private after(ms: number, fn: () => void): ReturnType<typeof setTimeout> {
    const t = setTimeout(() => {
      this.timers.delete(t);
      if (!this.dead) fn();
    }, ms);
    this.timers.add(t);
    return t;
  }

  /** Shows the next card, collapsing a burst into one. */
  private drain(): void {
    if (this.held || this.card !== null || this.pending.length === 0) return;
    // MORE THAN `unlockCollapseAt`, as achievements.md §4 and §8 both
    // state it. At the figure itself the queue still plays one card each.
    if (this.pending.length > ACHIEVEMENT_LOOK.unlockCollapseAt) {
      const burst = this.pending;
      this.pending = [];
      // Every unlock of the burst still leaves its own line, because the
      // record is what a player goes back to; only the ceremony collapses.
      this.show(collapsedLines(burst.length), burst.map(unlockNotice));
      return;
    }
    const u = this.pending.shift()!;
    this.show(linesOf(u), [unlockNotice(u)]);
  }

  /**
   * Builds one card, holds it, then contracts it into the notices column.
   *
   * `lines` is what the card says and `notices` is what it leaves behind —
   * one line for a single unlock, one per unlock for a collapsed burst.
   */
  private show(lines: UnlockLines, notices: string[]): void {
    const ms = ACHIEVEMENT_LOOK.unlockMs;
    const land = ACHIEVEMENT_LOOK.unlockLandMs;
    const travel = ACHIEVEMENT_LOOK.unlockTravelMs;
    const el = document.createElement('div');
    el.id = 'unlock';
    const sheet = (this.deps.sheet ?? isSheet)();
    const still = (this.deps.reduced ?? reducedMotion)();
    el.className = `panel unlock ${UNLOCK_CLASS}`
      + `${sheet ? ' sheet' : ''}${still ? ' still' : ''}`;
    // Every length is a PROPERTY and never an inline `animation-duration`:
    // the landing, the flash, the travel and the reduced-motion fade are
    // four animations, and an inline duration would stand over each one.
    el.style.setProperty('--unlock-ms', `${ms}ms`);
    el.style.setProperty('--unlock-land-ms', `${land}ms`);
    el.style.setProperty('--unlock-travel-ms', `${travel}ms`);
    // The scale the stamp lands at. The travel leaves by the same figure
    // the other way, so the card contracts as far below 1× as it arrived
    // above it, and one value states both.
    el.style.setProperty('--unlock-scale', String(ACHIEVEMENT_LOOK.unlockScale));
    // The desktop card is `unlock_desktop_size` times the phone card in
    // width and in height (R963). The stylesheet multiplies every length
    // by the property, and the badge bakes at the same factor so the
    // larger card carries a sharp badge and not a scaled bitmap.
    const size = sheet ? 1 : ACHIEVEMENT_LOOK.unlockDesktopSize;
    el.style.setProperty('--unlock-size', String(size));

    el.append(badgeCanvas(lines.category, lines.badgeGrade, Math.round(CARD_BADGE_PX * size)));
    const body = document.createElement('div');
    body.className = 'body';
    body.append(
      text('eyebrow', lines.title),
      text('name', lines.name),
      text('desc', lines.desc),
      text('grade', lines.grade),
      text('figures', lines.figures),
      bar(lines.fill),
      text('next', lines.next),
    );
    el.append(body);

    this.deps.host.appendChild(el);
    this.card = el;
    this.deps.play(UNLOCK_SFX);

    // The landing and the hold, then the travel, then the line. The card
    // stays `this.card` through the travel: one ceremony at a time means
    // one card on screen, and a card on its way to the column is still on
    // screen.
    let leaving = false;
    const leave = (): void => {
      if (leaving) return;
      leaving = true;
      this.aim(el, still);
      this.after(travel, () => {
        el.remove();
        if (this.card === el) this.card = null;
        for (const line of notices) {
          this.deps.notice(line, { key: UNLOCK_CLASS });
        }
        // The gap is the quiet between two ceremonies. It runs even when
        // nothing waits, which costs one timer and keeps the rule in one
        // place.
        this.resting = true;
        this.after(ACHIEVEMENT_LOOK.unlockGapMs, () => {
          this.resting = false;
          this.drain();
        });
      });
    };
    const hold = this.after(land + ms, leave);
    // A tap minimizes the card (R1442): the hold ends now and the travel
    // starts. The tap stops at the card, so it never reaches the map as
    // an order.
    el.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (leaving) return;
      clearTimeout(hold);
      this.timers.delete(hold);
      leave();
    });
  }

  /**
   * Points the card at the notices column and starts the travel.
   *
   * ONE measurement, at the start of the travel: the column's rectangle
   * and the card's own. The delta goes on the card as two properties and
   * the stylesheet's `unlock-travel` reads them, so no frame of the travel
   * writes a style (ui-map-interface.md §10.1, §10.2).
   *
   * The card's top-right corner goes to the column's top-right corner,
   * which is where the line it becomes will stand. In the sheet form
   * factor the stylesheet puts `#notices` in the sheet's own notice lane,
   * so the same measurement answers both.
   *
   * Reduced motion travels nowhere: the card fades where it landed, and
   * the delta is left unwritten so the keyframe falls back to zero.
   */
  private aim(el: HTMLElement, still: boolean): void {
    if (!still) {
      const host = (this.deps.column ?? noticesColumn)();
      // Chrome px (R1556): the delta is a length of the chrome's own.
      const card = chromeRect(el);
      if (host && card.width > 0) {
        const to = chromeRect(host);
        el.style.setProperty('--unlock-dx', `${Math.round(to.right - card.right)}px`);
        el.style.setProperty('--unlock-dy', `${Math.round(to.top - card.top)}px`);
      }
    }
    el.classList.add('travel');
  }
}

/** The notices column of the game shell, or null on a page that has none. */
function noticesColumn(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.getElementById('notices');
}

/** One line of the card. An empty line is drawn and hidden by the
 *  stylesheet, so the card's rows do not move between two cards. */
function text(cls: string, s: string): HTMLElement {
  const el = document.createElement('div');
  el.className = cls;
  el.textContent = s;
  if (s === '') el.hidden = true;
  return el;
}

/** The progress bar from the crossed threshold to the next one. The fill
 *  rides one custom property, written once when the card is built. A card
 *  with no ladder draws the bar hidden, like an empty line. */
function bar(fill: number | null): HTMLElement {
  const el = document.createElement('div');
  el.className = 'bar';
  if (fill === null) el.hidden = true;
  else el.style.setProperty('--fill', fill.toFixed(3));
  return el;
}

// ---------------------------------------------------------------------------
// The preview knob (R938)
// ---------------------------------------------------------------------------

/**
 * `#unlock=<code>:<grade>[,<code>:<grade>…]` on the game address.
 *
 * It pushes those unlocks after the hello, so a person can judge the card
 * against its mockup with no server, no grant and no join. It is a PREVIEW
 * SWITCH and never a setting: the hash is the only way in, it changes
 * nothing on the wire, and it is not stored.
 *
 * The knob is read here and not in `fast/knobs.ts`, because that reader
 * splits a hash on a comma and this value carries commas of its own.
 */
export function readUnlockKnob(
  hash: string = typeof location === 'undefined' ? '' : location.hash,
): Unlock[] {
  const m = /(?:^|[#&;]|%20|\s)unlock=([^&;\s]+)/i.exec(hash);
  if (!m) return [];
  // A malformed `%` escape means no knob, and never a throw at boot (R3027).
  let list: string;
  try {
    list = decodeURIComponent(m[1]!);
  } catch {
    return [];
  }
  const out: Unlock[] = [];
  for (const part of list.split(',')) {
    const [code, g] = part.split(':');
    if (!code) continue;
    const row = rowByCode(code);
    if (!row) continue;
    const grade = Math.max(1, Math.min(ACHIEVEMENT_GRADES.length, Number.parseInt(g ?? '1', 10) || 1));
    out.push({
      code,
      grade,
      // The value the fly-in prints is the threshold the grade stands at,
      // which is the honest figure for a preview: no account crossed it.
      value: row.thresholds[grade - 1] ?? 0,
      kind: row.kind === 'recognition' ? KIND_RECOGNITION : row.kind === 'journey' ? 1 : 0,
    });
  }
  return out;
}
